<template>
    <Head :title="`Asset: ${asset.name}`" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Back Button -->
            <div class="mb-4">
                <Link
                    :href="route('settings.maintenance.assets.index')"
                    class="inline-flex items-center text-sm text-gray-600 dark:text-secondary-400 hover:text-gray-900 dark:hover:text-white"
                >
                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Assets
                </Link>
            </div>

            <!-- Asset Header -->
            <div class="bg-white dark:bg-secondary-800 rounded-lg shadow overflow-hidden mb-6">
                <div class="p-6">
                    <div class="flex flex-col md:flex-row gap-6">
                        <!-- Asset Image -->
                        <div class="flex-shrink-0">
                            <div v-if="asset.image" class="w-48 h-48 rounded-lg overflow-hidden">
                                <img
                                    :src="asset.image_url"
                                    :alt="asset.name"
                                    class="w-full h-full object-cover"
                                />
                            </div>
                            <div v-else class="w-48 h-48 bg-gray-200 dark:bg-secondary-700 rounded-lg flex items-center justify-center">
                                <svg class="w-20 h-20 text-gray-400 dark:text-secondary-500" fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                </svg>
                            </div>
                        </div>

                        <!-- Asset Info -->
                        <div class="flex-1">
                            <div class="flex items-start justify-between">
                                <div>
                                    <p class="text-sm text-gray-500 dark:text-secondary-400">{{ asset.asset_tag }}</p>
                                    <div class="flex items-center gap-3 mt-1">
                                        <h1 class="text-3xl font-bold text-gray-900 dark:text-white">{{ asset.name }}</h1>
                                        <InfoButton title="Asset Details & Management" variant="default">
                                            <p>
                                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                                The Asset Details page provides a complete view of a single asset's information,
                                                maintenance schedules, work order history, and documentation. Use the tabs to
                                                navigate between different aspects of the asset lifecycle. This centralized view
                                                helps you track asset performance, maintenance costs, and reliability over time.
                                            </p>
                                            <p>
                                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                                • <strong>Quick Actions:</strong> Use header buttons to generate QR codes, edit
                                                  asset details, or create work orders<br>
                                                • <strong>Quick Stats:</strong> View maintenance costs, downtime, work order count,
                                                  and asset age at a glance<br>
                                                • <strong>Details Tab:</strong> View all asset information including basic details,
                                                  equipment specifications, financial data, and meter configuration<br>
                                                • <strong>PM Schedule Tab:</strong> Manage preventive maintenance schedules (Note:
                                                  PM Plans must be created from Settings → Maintenance → PM Plans first)<br>
                                                • <strong>Maintenance History Tab:</strong> Review completed maintenance work<br>
                                                • <strong>Work Orders Tab:</strong> View and create work orders for this asset<br>
                                                • <strong>Documents Tab:</strong> Upload manuals, warranties, and maintenance
                                                  procedures<br>
                                                • <strong>History Tab:</strong> Track all changes made to the asset
                                            </p>
                                            <p>
                                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                                • Generate <strong>QR codes</strong> to enable mobile access to asset information<br>
                                                • Monitor <strong>warranty expiration</strong> in Details tab to schedule warranty
                                                  work before it expires<br>
                                                • <strong>Quick Stats</strong> provide instant visibility into asset performance
                                                  and maintenance burden<br>
                                                • Review <strong>maintenance history</strong> regularly to identify recurring issues<br>
                                                • Keep <strong>documents up to date</strong> so technicians have current manuals
                                                  and procedures<br>
                                                • Use <strong>parent-child relationships</strong> to track components and
                                                  subsystems separately
                                            </p>
                                        </InfoButton>
                                    </div>
                                    <div class="mt-2 flex items-center space-x-3">
                                        <span
                                            :class="[
                                                'px-3 py-1 text-sm font-medium rounded-full',
                                                getStatusClass(asset.status)
                                            ]"
                                        >
                                            {{ asset.status_label }}
                                        </span>
                                        <span
                                            :class="[
                                                'px-3 py-1 text-sm font-medium rounded-full',
                                                getCriticalityClass(asset.criticality)
                                            ]"
                                        >
                                            {{ asset.criticality_label }} Criticality
                                        </span>
                                    </div>
                                </div>

                                <!-- Action Buttons -->
                                <div class="flex items-center space-x-2">
                                    <a
                                        :href="route('settings.maintenance.assets.qrCode', asset.id)"
                                        class="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-secondary-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600"
                                    >
                                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                                        </svg>
                                        QR Code
                                    </a>
                                    <Link
                                        v-if="$page.props.auth.permissions.includes('maintenance.assets.manage')"
                                        :href="route('settings.maintenance.assets.edit', asset.id)"
                                        class="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-secondary-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600"
                                    >
                                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                        Edit
                                    </Link>
                                    <Link
                                        :href="getWorkOrderCreateUrl()"
                                        class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                    >
                                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                        </svg>
                                        Create Work Order
                                    </Link>
                                </div>
                            </div>

                            <!-- Quick Stats -->
                            <div class="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
                                <div class="border-l-4 border-green-500 pl-4">
                                    <p class="text-sm text-gray-600 dark:text-secondary-400">Total Maintenance Cost</p>
                                    <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                                        {{ formatMoney(metrics.total_maintenance_cost) }}
                                    </p>
                                </div>
                                <div class="border-l-4 border-yellow-500 pl-4">
                                    <p class="text-sm text-gray-600 dark:text-secondary-400">Total Downtime</p>
                                    <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                                        {{ metrics.total_downtime_hours }} hrs
                                    </p>
                                </div>
                                <div class="border-l-4 border-blue-500 pl-4">
                                    <p class="text-sm text-gray-600 dark:text-secondary-400">Work Orders</p>
                                    <p class="text-2xl font-semibold text-gray-900 dark:text-white">
                                        {{ metrics.work_order_count }}
                                    </p>
                                </div>
                                <div class="border-l-4 border-purple-500 pl-4">
                                    <p class="text-sm text-gray-600 dark:text-secondary-400">Asset Age</p>
                                    <p class="text-2xl font-semibold text-gray-900 dark:text-white" v-if="metrics.age_in_days !== null">
                                        {{ metrics.age_in_days }} days
                                    </p>
                                    <p class="text-sm font-medium text-gray-500 dark:text-secondary-400" v-else>
                                        N/A - purchase date missing
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Tabs -->
            <div class="bg-white dark:bg-secondary-800 rounded-lg shadow">
                <div class="border-b border-gray-200 dark:border-secondary-700">
                    <nav class="-mb-px flex space-x-8 px-6">
                        <button
                            v-for="tab in tabs"
                            :key="tab.id"
                            @click="activeTab = tab.id"
                            :class="[
                                'py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap',
                                activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                    : 'border-transparent text-gray-500 dark:text-secondary-400 hover:text-gray-700 dark:hover:text-secondary-300 hover:border-gray-300 dark:hover:border-secondary-600'
                            ]"
                        >
                            {{ tab.label }}
                        </button>
                    </nav>
                </div>

                <!-- Tab Content -->
                <div class="p-6">
                    <!-- Details Tab -->
                    <div v-if="activeTab === 'details'">
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Basic Information -->
                            <div>
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>
                                <dl class="space-y-3">
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Category</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ asset.category?.path_name || '-' }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Location</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ asset.location?.path_with_type || '-' }}</dd>
                                    </div>
                                </dl>
                            </div>

                            <!-- Equipment Details -->
                            <div>
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Equipment Details</h3>
                                <dl class="space-y-3">
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Manufacturer</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ asset.manufacturer || '-' }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Model</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ asset.model || '-' }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Serial Number</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ asset.serial_number || '-' }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Barcode</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ asset.barcode || '-' }}</dd>
                                    </div>
                                </dl>
                            </div>

                            <!-- Financial Information -->
                            <div>
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Financial Information</h3>
                                <dl class="space-y-3">
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Purchase Date</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ asset.purchase_date ? formatDate(asset.purchase_date) : '-' }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Installation Date</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ asset.installation_date ? formatDate(asset.installation_date) : '-' }}</dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Purchase Cost</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                                            {{ asset.purchase_cost ? formatMoney(asset.purchase_cost) : '-' }}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Current Value</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                                            {{ asset.current_value ? formatMoney(asset.current_value) : '-' }}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Warranty Expiry</dt>
                                        <dd class="mt-1 text-sm">
                                            <span :class="isWarrantyExpired() ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'">
                                                {{ asset.warranty_expiry_date ? formatDate(asset.warranty_expiry_date) : '-' }}
                                            </span>
                                            <span v-if="metrics.warranty_days_remaining !== null" class="ml-2 text-xs text-gray-500 dark:text-secondary-400">
                                                ({{ metrics.warranty_days_remaining }} days {{ metrics.warranty_days_remaining >= 0 ? 'remaining' : 'expired' }})
                                            </span>
                                        </dd>
                                    </div>
                                </dl>
                            </div>

                            <!-- Meter Configuration -->
                            <div>
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Meter Configuration</h3>
                                <dl class="space-y-3">
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Meter Type</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ asset.meter_type || 'None' }}</dd>
                                    </div>
                                    <div v-if="asset.meter_type && asset.meter_type !== 'none'">
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">Current Reading</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                                            {{ asset.current_meter_reading || '0' }} {{ asset.meter_unit }}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">PM Schedules</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">
                                            {{ pmSchedules.length }} active {{ pmSchedules.length === 1 ? 'schedule' : 'schedules' }}
                                        </dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <!-- Specifications -->
                        <div v-if="asset.specifications" class="mt-6">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Specifications</h3>
                            <div class="bg-gray-50 dark:bg-secondary-750 rounded-lg p-4">
                                <dl class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div v-for="(value, key) in asset.specifications" :key="key">
                                        <dt class="text-sm font-medium text-gray-500 dark:text-secondary-400">{{ key }}</dt>
                                        <dd class="mt-1 text-sm text-gray-900 dark:text-white">{{ value }}</dd>
                                    </div>
                                </dl>
                            </div>
                        </div>

                        <!-- Notes -->
                        <div v-if="asset.notes" class="mt-6">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes</h3>
                            <p class="text-sm text-gray-600 dark:text-secondary-400 whitespace-pre-wrap">{{ asset.notes }}</p>
                        </div>
                    </div>

                    <!-- PM Schedule Tab -->
                    <div v-if="activeTab === 'pm-schedule'">
                        <div class="flex justify-between items-center mb-4">
                            <div class="flex items-center gap-3">
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">PM Schedules</h3>
                                <InfoButton title="PM Schedules & How to Add" variant="default" size="sm">
                                    <p>
                                        <strong class="text-secondary-900 dark:text-white">About this tab</strong><br>
                                        The PM Schedule tab displays all preventive maintenance schedules linked to this asset.
                                        PM schedules automatically generate work orders based on time intervals (calendar-based)
                                        or meter readings (usage-based). This proactive approach reduces unexpected breakdowns,
                                        extends asset life, and optimizes maintenance costs.
                                    </p>
                                    <p>
                                        <strong class="text-secondary-900 dark:text-white">How to add PM schedules</strong><br>
                                        <strong>IMPORTANT:</strong> You cannot create PM schedules directly on this page.
                                        To add PM schedules to this asset, follow these steps:<br>
                                        <br>
                                        <strong>Step 1: Create a PM Plan</strong><br>
                                        Navigate to <strong>Settings → Maintenance → PM Plans</strong> to create a reusable
                                        PM plan template<br>
                                        <br>
                                        <strong>Step 2: Define the Plan</strong><br>
                                        Set up maintenance tasks, frequency (daily, weekly, monthly, etc.), priority, estimated
                                        hours, and create a task checklist<br>
                                        <br>
                                        <strong>Step 3: Link to Asset</strong><br>
                                        When creating or editing the PM Plan, you can select assets in the "Assets" field.
                                        Alternatively, use the "Add PM Plan" button on this page to link an existing plan<br>
                                        <br>
                                        <strong>Step 4: Set Schedule Details</strong><br>
                                        Configure the next PM date and work days required. The system will automatically
                                        calculate future dates and generate work orders<br>
                                        <br>
                                        <em>Example: Create a "Monthly HVAC Filter Check" PM Plan with task checklist, then
                                        link it to all HVAC assets. The system automatically schedules monthly work orders
                                        for each asset.</em>
                                    </p>
                                    <p>
                                        <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                        • <strong>PM Plans are reusable</strong> - Create once and link to multiple similar
                                          assets<br>
                                        • <strong>Monitor next PM dates</strong> - This tab shows when work orders will be
                                          generated, allowing you to plan resources<br>
                                        • <strong>Calendar vs. meter-based</strong> - Use calendar schedules for time-dependent
                                          tasks (monthly inspections) and meter-based for usage-dependent tasks (every 1,000
                                          hours)<br>
                                        • <strong>Work order integration</strong> - PM schedules create work orders in "Open"
                                          status, ready for assignment<br>
                                        • <strong>Generate WO manually</strong> - Use "Generate WO" button to create a work
                                          order ahead of schedule if needed<br>
                                        • <strong>Edit schedules</strong> - Adjust next PM date or work days without changing
                                          the underlying PM Plan
                                    </p>
                                </InfoButton>
                            </div>
                            <button
                                @click="showAddPmModal = true"
                                class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                            >
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                </svg>
                                Add PM Plan
                            </button>
                        </div>

                        <!-- PM Schedules Table -->
                        <div v-if="pmSchedules.length > 0" class="overflow-x-auto">
                            <table class="min-w-full divide-y divide-gray-200 dark:divide-secondary-700">
                                <thead class="bg-gray-50 dark:bg-secondary-750">
                                    <tr>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                            PM Plan
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                            Last PM Date
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                            Next PM Date
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                            Next Notification
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                            Work Days
                                        </th>
                                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-secondary-400 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody class="bg-white dark:bg-secondary-800 divide-y divide-gray-200 dark:divide-secondary-700">
                                    <tr v-for="schedule in pmSchedules" :key="schedule.id">
                                        <td class="px-6 py-4 whitespace-nowrap">
                                            <div class="text-sm font-medium text-gray-900 dark:text-white">{{ schedule.pm_plan?.name }}</div>
                                            <div class="text-sm text-gray-500 dark:text-secondary-400">{{ schedule.pm_plan?.frequency_label }}</div>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {{ schedule.last_pm_date ? formatDate(schedule.last_pm_date) : '-' }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {{ schedule.next_pm_date ? formatDate(schedule.next_pm_date) : '-' }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {{ schedule.next_notification_date ? formatDate(schedule.next_notification_date) : '-' }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                            {{ schedule.work_days || '-' }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap">
                                            <span
                                                :class="[
                                                    'px-2 py-1 text-xs font-medium rounded-full',
                                                    schedule.is_active ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' : 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300'
                                                ]"
                                            >
                                                {{ schedule.is_active ? 'Active' : 'Inactive' }}
                                            </span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-3">
                                            <Link
                                                v-if="schedule.is_active && $page.props.auth.permissions.includes('maintenance.work_orders.create_pm_order')"
                                                :href="route('work-orders.createFromPmSchedule', schedule.id)"
                                                class="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                                            >
                                                Generate WO
                                            </Link>
                                            <button
                                                @click="editPmSchedule(schedule)"
                                                class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                @click="deletePmSchedule(schedule)"
                                                class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Empty State -->
                        <div v-else class="text-center py-12 bg-gray-50 dark:bg-secondary-750 rounded-lg">
                            <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                            </svg>
                            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No PM schedules</h3>
                            <p class="mt-1 text-sm text-gray-500 dark:text-secondary-400">Get started by adding a PM plan to this asset.</p>
                            <div class="mt-6">
                                <button
                                    @click="showAddPmModal = true"
                                    class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    Add PM Plan
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- Maintenance History Tab -->
                    <div v-if="activeTab === 'maintenance-history'">
                        <div class="flex items-center gap-3 mb-4">
                            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Maintenance History</h3>
                            <InfoButton title="Maintenance History" variant="default" size="sm">
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">About this tab</strong><br>
                                    The Maintenance History tab shows completed maintenance work orders for this asset.
                                    Review past preventive maintenance, repairs, inspections, and emergency work to identify
                                    trends, recurring issues, and overall asset reliability. This historical data is valuable
                                    for optimizing maintenance strategies and planning future work.
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                    • <strong>Review completed work:</strong> Each entry shows work order number, description,
                                      completion date, and maintenance type<br>
                                    • <strong>Click for details:</strong> Select any work order to view complete details
                                      including technician notes, parts used, and labor hours<br>
                                    • <strong>Identify patterns:</strong> Look for recurring issues that may indicate need
                                      for upgrades or process changes
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                    • <strong>Track failure patterns</strong> - Multiple work orders for the same issue
                                      may justify replacement<br>
                                    • <strong>Review maintenance costs</strong> - High frequency may indicate need for
                                      preventive upgrades<br>
                                    • <strong>Maintenance types</strong> help identify whether asset requires more
                                      preventive vs. corrective work
                                </p>
                            </InfoButton>
                        </div>
                        <div v-if="maintenanceHistory.length > 0" class="space-y-4">
                            <div
                                v-for="wo in maintenanceHistory"
                                :key="wo.id"
                                class="border-l-4 border-gray-300 dark:border-secondary-600 pl-4 py-2"
                            >
                                <div class="flex items-start justify-between">
                                    <div>
                                        <Link
                                            :href="route('work-orders.show', wo.id)"
                                            class="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                        >
                                            {{ wo.work_order_number }}
                                        </Link>
                                        <p class="text-sm text-gray-600 dark:text-secondary-400 mt-1">{{ wo.title }}</p>
                                        <p class="text-xs text-gray-500 dark:text-secondary-400 mt-1">
                                            {{ formatDate(wo.actual_end) }} • {{ wo.type_label }}
                                        </p>
                                    </div>
                                    <span
                                        :class="[
                                            'px-2 py-1 text-xs font-medium rounded-full',
                                            getWorkOrderStatusClass(wo.status)
                                        ]"
                                    >
                                        {{ wo.status_label }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <p v-else class="text-sm text-gray-500 dark:text-secondary-400">No maintenance history available.</p>
                    </div>

                    <!-- Work Orders Tab -->
                    <div v-if="activeTab === 'work-orders'">
                        <div class="flex justify-between items-center mb-4">
                            <div class="flex items-center gap-3">
                                <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Work Orders</h3>
                                <InfoButton title="Asset Work Orders" variant="default" size="sm">
                                    <p>
                                        <strong class="text-secondary-900 dark:text-white">About this tab</strong><br>
                                        The Work Orders tab displays all work orders associated with this asset, including
                                        preventive maintenance (generated from PM schedules), corrective maintenance (repairs),
                                        inspections, and emergency work. Track open, in-progress, and completed work orders
                                        to maintain visibility into asset maintenance activities and identify reliability issues.
                                    </p>
                                    <p>
                                        <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                        • <strong>View all work orders:</strong> See open, in-progress, and completed work
                                          orders related to this asset<br>
                                        • <strong>Create new work orders:</strong> Click "New Work Order" button to create
                                          corrective, preventive, or emergency maintenance requests<br>
                                        • <strong>Track status:</strong> Work orders display status badges (Open, Assigned,
                                          In Progress, Completed) to show current state<br>
                                        • <strong>View details:</strong> Click any work order to view full details, add notes,
                                          attach documents, or update status<br>
                                        • <strong>Work order types:</strong> Corrective (repairs), Preventive (scheduled),
                                          Predictive (data-driven), Inspection, Calibration, Emergency
                                    </p>
                                    <p>
                                        <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                        • Monitor <strong>recurring issues</strong> - Multiple work orders for the same
                                          problem may indicate need for replacement or process change<br>
                                        • Review <strong>maintenance costs</strong> - High work order frequency may justify
                                          preventive upgrades<br>
                                        • Use <strong>priority levels</strong> to ensure critical issues get immediate attention<br>
                                        • <strong>PM-generated work orders</strong> appear automatically based on PM schedules
                                    </p>
                                </InfoButton>
                            </div>
                            <Link
                                :href="getWorkOrderCreateUrl()"
                                class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                            >
                                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                                </svg>
                                New Work Order
                            </Link>
                        </div>

                        <!-- Work Orders List -->
                        <div v-if="openWorkOrders.length > 0" class="space-y-3">
                            <div
                                v-for="wo in openWorkOrders"
                                :key="wo.id"
                                class="border border-gray-200 dark:border-secondary-700 rounded-lg p-4 hover:shadow-md transition-shadow"
                            >
                                <div class="flex items-start justify-between">
                                    <div class="flex-1">
                                        <Link
                                            :href="route('work-orders.show', wo.id)"
                                            class="font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                        >
                                            {{ wo.work_order_number }}
                                        </Link>
                                        <p class="text-sm text-gray-600 dark:text-secondary-400 mt-1">{{ wo.title }}</p>
                                        <div class="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-secondary-400">
                                            <span>{{ wo.type_label }}</span>
                                            <span>•</span>
                                            <span>{{ wo.priority_label }}</span>
                                            <span>•</span>
                                            <span>Created {{ formatDate(wo.created_at) }}</span>
                                        </div>
                                    </div>
                                    <span
                                        :class="[
                                            'px-2 py-1 text-xs font-medium rounded-full',
                                            getWorkOrderStatusClass(wo.status)
                                        ]"
                                    >
                                        {{ wo.status_label }}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <p v-else class="text-sm text-gray-500 dark:text-secondary-400">No open work orders for this asset.</p>
                    </div>

                    <!-- Documents Tab -->
                    <div v-if="activeTab === 'documents'">
                        <div class="text-center py-12">
                            <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                            </svg>
                            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No documents</h3>
                            <p class="mt-1 text-sm text-gray-500 dark:text-secondary-400">Upload manuals, warranties, and other documents.</p>
                            <div class="mt-6">
                                <button class="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-secondary-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600">
                                    <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                    Upload Document
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- History Tab -->
                    <div v-if="activeTab === 'history'">
                        <div class="text-center py-12">
                            <svg class="mx-auto h-12 w-12 text-gray-400 dark:text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">No history available</h3>
                            <p class="mt-1 text-sm text-gray-500 dark:text-secondary-400">Asset change history will appear here.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Add/Edit PM Schedule Modal -->
    <div v-if="showAddPmModal || showEditPmModal" class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50" @click="closeModal">
        <div class="bg-white dark:bg-secondary-800 rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" @click.stop>
            <form @submit.prevent="savePmSchedule">
                <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {{ showEditPmModal ? 'Edit PM Schedule' : 'Add PM Plan to Asset' }}
                </h3>

                <div class="space-y-4">
                    <!-- PM Plan Selection -->
                    <div>
                        <label for="pm_plan_id" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                            PM Plan <span class="text-red-500">*</span>
                        </label>
                        <select
                            id="pm_plan_id"
                            v-model="pmForm.pm_plan_id"
                            @change="onPmPlanChange"
                            :disabled="showEditPmModal"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                            :class="{ 'bg-gray-100': showEditPmModal }"
                        >
                            <option value="">Select PM Plan</option>
                            <option
                                v-for="plan in availablePmPlans"
                                :key="plan.id"
                                :value="plan.id"
                            >
                                {{ plan.name }} ({{ plan.frequency_label }})
                            </option>
                        </select>
                        <p v-if="pmFormErrors.pm_plan_id" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ pmFormErrors.pm_plan_id }}</p>
                    </div>

                    <!-- Plan Description -->
                    <div v-if="selectedPmPlan" class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <h4 class="text-sm font-medium text-blue-900 dark:text-blue-300 mb-2">Plan Details</h4>
                        <p class="text-sm text-blue-800 dark:text-blue-400 mb-2">{{ selectedPmPlan.description || 'No description available' }}</p>
                        <div class="grid grid-cols-2 gap-2 text-xs text-blue-700 dark:text-blue-400">
                            <div>
                                <span class="font-medium">Frequency:</span> {{ selectedPmPlan.frequency_label }}
                            </div>
                            <div>
                                <span class="font-medium">Priority:</span> {{ selectedPmPlan.priority_label }}
                            </div>
                            <div v-if="selectedPmPlan.estimated_hours">
                                <span class="font-medium">Est. Hours:</span> {{ selectedPmPlan.estimated_hours }}
                            </div>
                            <div v-if="selectedPmPlan.estimated_cost">
                                <span class="font-medium">Est. Cost:</span> {{ formatMoney(selectedPmPlan.estimated_cost) }}
                            </div>
                        </div>
                    </div>

                    <!-- Next PM Date -->
                    <div>
                        <label for="next_pm_date" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                            Next PM Date <span class="text-red-500">*</span>
                        </label>
                        <input
                            id="next_pm_date"
                            v-model="pmForm.next_pm_date"
                            type="date"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                        <p v-if="pmFormErrors.next_pm_date" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ pmFormErrors.next_pm_date }}</p>
                    </div>

                    <!-- Work Days -->
                    <div>
                        <label for="work_days" class="block text-sm font-medium text-gray-700 dark:text-secondary-300">
                            Work Days Required <span class="text-red-500">*</span>
                        </label>
                        <input
                            id="work_days"
                            v-model.number="pmForm.work_days"
                            type="number"
                            min="1"
                            required
                            class="mt-1 block w-full rounded-md border-gray-300 dark:border-secondary-600 bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                        <p v-if="pmFormErrors.work_days" class="mt-1 text-sm text-red-600 dark:text-red-400">{{ pmFormErrors.work_days }}</p>
                    </div>
                </div>

                <!-- Modal Buttons -->
                <div class="mt-6 flex justify-end space-x-3">
                    <button
                        type="button"
                        @click="closeModal"
                        :disabled="pmFormProcessing"
                        class="px-4 py-2 border border-gray-300 dark:border-secondary-600 rounded-md text-gray-700 dark:text-secondary-300 bg-white dark:bg-secondary-700 hover:bg-gray-50 dark:hover:bg-secondary-600"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        :disabled="pmFormProcessing"
                        class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                        {{ pmFormProcessing ? 'Saving...' : (showEditPmModal ? 'Update' : 'Add PM Plan') }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';
import { useCurrency } from '@/Composables/useCurrency';

const props = defineProps({
    asset: Object,
    maintenanceHistory: Array,
    openWorkOrders: Array,
    pmSchedules: Array,
    metrics: Object,
    pmPlans: Array,
    pmSettings: Object,
});

const { formatMoney } = useCurrency();

const toast = useToast();
const activeTab = ref('details');
const showAddPmModal = ref(false);
const showEditPmModal = ref(false);
const pmFormProcessing = ref(false);
const pmFormErrors = ref({});

const pmForm = ref({
    id: null,
    pm_plan_id: '',
    next_pm_date: '',
    work_days: 1,
});

const tabs = [
    { id: 'details', label: 'Details' },
    { id: 'pm-schedule', label: 'PM Schedule' },
    { id: 'maintenance-history', label: 'Maintenance History' },
    { id: 'work-orders', label: 'Work Orders' },
    { id: 'documents', label: 'Documents' },
    { id: 'history', label: 'History' },
];

// Filter out already assigned PM plans
const availablePmPlans = computed(() => {
    if (showEditPmModal.value) {
        // When editing, show all plans including the current one
        return props.pmPlans || [];
    }

    const assignedPlanIds = props.pmSchedules.map(s => s.pm_plan_id);
    return (props.pmPlans || []).filter(plan => !assignedPlanIds.includes(plan.id));
});

const selectedPmPlan = computed(() => {
    if (!pmForm.value.pm_plan_id) return null;
    return (props.pmPlans || []).find(plan => plan.id === pmForm.value.pm_plan_id);
});

const getWorkOrderCreateUrl = () => {
    let url = route('work-orders.create') + '?asset_id=' + props.asset.id;
    if (props.asset.location_id) {
        url += '&location_id=' + props.asset.location_id;
    }
    return url;
};

const onPmPlanChange = () => {
    // Clear plan description when selection changes
    pmFormErrors.value = {};
};

const editPmSchedule = (schedule) => {
    pmForm.value = {
        id: schedule.id,
        pm_plan_id: schedule.pm_plan_id,
        next_pm_date: schedule.next_pm_date || '',
        work_days: schedule.work_days || 1,
    };
    pmFormErrors.value = {};
    showEditPmModal.value = true;
};

const deletePmSchedule = (schedule) => {
    if (!confirm('Are you sure you want to remove this PM schedule from the asset?')) {
        return;
    }

    router.delete(route('settings.maintenance.pm-schedules.destroy', schedule.id), {
        onSuccess: () => {
            toast.success('PM schedule removed successfully');
        },
        onError: () => {
            toast.error('Failed to remove PM schedule');
        },
    });
};

const savePmSchedule = () => {
    pmFormProcessing.value = true;
    pmFormErrors.value = {};

    const data = {
        asset_id: props.asset.id,
        pm_plan_id: pmForm.value.pm_plan_id,
        next_pm_date: pmForm.value.next_pm_date,
        work_days: pmForm.value.work_days,
    };

    const url = showEditPmModal.value
        ? route('settings.maintenance.pm-schedules.update', pmForm.value.id)
        : route('settings.maintenance.pm-schedules.store');

    const method = showEditPmModal.value ? 'put' : 'post';

    router[method](url, data, {
        onSuccess: () => {
            toast.success(showEditPmModal.value ? 'PM schedule updated successfully' : 'PM plan assigned successfully');
            closeModal();
        },
        onError: (errors) => {
            pmFormErrors.value = errors;
            toast.error('Please check the form for errors');
        },
        onFinish: () => {
            pmFormProcessing.value = false;
        },
    });
};

const closeModal = () => {
    showAddPmModal.value = false;
    showEditPmModal.value = false;
    pmForm.value = {
        id: null,
        pm_plan_id: '',
        next_pm_date: '',
        work_days: 1,
    };
    pmFormErrors.value = {};
};

const getStatusClass = (status) => {
    const classes = {
        operational: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400',
        down: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
        maintenance: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
        retired: 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300',
        disposed: 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300',
    };
    return classes[status] || 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300';
};

const getCriticalityClass = (criticality) => {
    const classes = {
        low: 'bg-blue-100 text-blue-800',
        medium: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
        high: 'bg-orange-100 text-orange-800',
        critical: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
    };
    return classes[criticality] || 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300';
};

const getWorkOrderStatusClass = (status) => {
    const classes = {
        draft: 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300',
        open: 'bg-blue-100 text-blue-800',
        assigned: 'bg-cyan-100 text-cyan-800',
        in_progress: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
        on_hold: 'bg-orange-100 text-orange-800',
        pending_review: 'bg-purple-100 text-purple-800',
        completed: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400',
        closed: 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300',
    };
    return classes[status] || 'bg-gray-100 dark:bg-secondary-700 text-gray-800 dark:text-secondary-300';
};

const isWarrantyExpired = () => {
    if (!props.asset.warranty_expiry_date) return false;
    return new Date(props.asset.warranty_expiry_date) < new Date();
};

const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = d.toLocaleDateString('en-US', { month: 'short' });
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
};
</script>
